import React, { useState } from "react";
import { useL } from "@/lib/useL";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Copy, Link2, Mail, MessageCircle, Share2, FileText, Loader2, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { trackShare } from "@/lib/analytics";
import { API } from "@/lib/api";
import { Maximize2, QrCode } from "lucide-react";
import { markdownToPlain } from "@/lib/shareText";

const SHORT_TEXT = 1500;
const MOBILE_MAIL_MAX = 20000;

async function copyText(value) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

const isMobile = () => typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const Network = ({ label, onClick, testid, disabled, hint }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    data-tip={hint}
    className="px-2 py-2.5 border border-border font-mono uppercase tracking-[0.12em] text-[10px] font-semibold hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors disabled:opacity-60 disabled:border-dashed disabled:cursor-not-allowed"
  >
    {label}
  </button>
);

// Cut at a line break near the limit so the text never stops mid-sentence.
const shorten = (t, max = SHORT_TEXT) => {
  if (t.length <= max) return t;
  const cut = t.lastIndexOf("\n", max);
  return `${t.slice(0, cut > max * 0.5 ? cut : max).trimEnd()}\n…`;
};

const Option = ({ icon: Icon, label, hint, onClick, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className="w-full flex items-center gap-3 px-4 py-3 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
  >
    <Icon className="w-4 h-4 shrink-0" />
    <span className="flex-1">
      <span className="block font-mono uppercase tracking-[0.14em] text-[11px] font-semibold">{label}</span>
      {hint && <span className="block text-[11px] opacity-70 mt-0.5">{hint}</span>}
    </span>
  </button>
);

// Share window that works on every device (no reliance on navigator.share only).
export function ShareDialog({ open, onOpenChange, title, text, isPublic, publicUrl, onEnsurePublic, sharing, needsAccount = false, part = false }) {
  const L = useL();
  const sign = part ? L("— Réalisé avec SUX1", "— Made with SUX1") : L("— Résumé réalisé avec SUX1", "— Summary made with SUX1");
  const [creating, setCreating] = useState(false);
  const [qrFull, setQrFull] = useState(false);
  const url = isPublic ? publicUrl : null;
  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const [mainMd, ...extrasMd] = (text || "").split(/\n\n(?=## Complément : )/);
  const plainMain = markdownToPlain(mainMd);
  const plainExtras = extrasMd.map(markdownToPlain).join("\n\n");
  const plain = [plainMain, plainExtras].filter(Boolean).join("\n\n");
  // Added answers (QCM, email...) are kept whole: the main summary is shortened first.
  const fit = (max) => {
    if (plain.length <= max) return plain;
    const room = max - plainExtras.length - 4;
    return plainExtras && room > 300 ? `${shorten(plainMain, room)}\n\n${plainExtras}` : shorten(plain, max);
  };
  const cutText = fit(SHORT_TEXT);
  const isCut = cutText.length < plain.length;
  const linkLine = url ? `${isCut ? L("Lire la suite et le résumé complet", "Read the rest and the full summary") : L("Lire le résumé en ligne", "Read the summary online")}${L(" :", ":")} ${url}` : "";
  // Each channel accepts a different message size (mailto links are the most limited).
  const bodyFor = (max) => {
    const cut = fit(max);
    const line = url ? `${cut.length < plain.length ? L("Lire la suite et le résumé complet", "Read the rest and the full summary") : L("Lire le résumé en ligne", "Read the summary online")}${L(" :", ":")} ${url}` : "";
    return [title, cut, line, sign].filter(Boolean).join("\n\n");
  };
  const body = [title, cutText, linkLine, sign].filter(Boolean).join("\n\n");
  const tooLongNoLink = !url && plain.length > SHORT_TEXT;

  const done = (kind, msg) => {
    trackShare(kind);
    if (msg) toast.success(msg);
  };

  const createLink = async () => {
    setCreating(true);
    try {
      await onEnsurePublic();
    } catch {
      toast.error(L("Impossible de générer le lien public.", "Could not create the public link."));
    } finally {
      setCreating(false);
    }
  };

  const onCopyLink = async () => (await copyText(url)) ? done("copy_link", L("Lien copié. Collez-le où vous voulez.", "Link copied. Paste it wherever you like.")) : toast.error(L("Copie impossible.", "Copy failed."));
  const onCopyText = async () =>
    (await copyText([title, plain, url ? `Lien : ${url}` : ""].filter(Boolean).join("\n\n")))
      ? done("copy", part ? L("Texte copié. Collez-le dans un email, un message ou un document.", "Text copied. Paste it into an email, a message or a document.") : L("Résumé copié. Collez-le dans un email, un message ou un document.", "Summary copied. Paste it into an email, a message or a document."))
      : toast.error("Copie impossible.");
  // Phone mail apps accept long messages; desktop mail links are limited to ~2,000 characters.
  // Desktop mail links are cut around 2,000 characters: the full text goes to the clipboard instead.
  const onEmail = async () => {
    let mailBody = isMobile() ? bodyFor(MOBILE_MAIL_MAX) : body;
    if (!isMobile() && plain.length > SHORT_TEXT) {
      const full = [title, plain, url ? `${L("Lien", "Link")} : ${url}` : "", sign].filter(Boolean).join("\n\n");
      if (await copyText(full)) {
        mailBody = [title, L("Le texte complet vient d'être copié : collez-le ici (Ctrl+V).", "The full text has just been copied: paste it here (Ctrl+V)."), linkLine, sign].filter(Boolean).join("\n\n");
        toast.success(L("Texte complet copié : dans l'email, faites Ctrl+V pour le coller.", "Full text copied: in the email, press Ctrl+V to paste it."));
      }
    }
    window.location.href = `mailto:?subject=${encodeURIComponent(title || L("Résumé SUX1", "SUX1 summary"))}&body=${encodeURIComponent(mailBody)}`;
    done("email");
  };
  const onWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(bodyFor(4000))}`, "_blank", "noopener,noreferrer");
    done("whatsapp");
  };
  const openUrl = (href, kind) => {
    window.open(href, "_blank", "noopener,noreferrer");
    done(kind);
  };
  const enc = encodeURIComponent;
  const shortBody = [title, fit(600), url ? `Lire la suite : ${url}` : ""].filter(Boolean).join("\n\n");
  const onGmail = () => openUrl(`https://mail.google.com/mail/?view=cm&fs=1&su=${enc(title || L("Résumé SUX1", "SUX1 summary"))}&body=${enc(bodyFor(isMobile() ? 12000 : 6000))}`, "gmail");
  const onFacebook = () => openUrl(`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, "facebook");
  const onMessenger = () => {
    window.location.href = `fb-messenger://share/?link=${enc(url)}`;
    done("messenger");
  };
  const onSms = () => {
    window.location.href = `sms:?&body=${enc(shortBody)}`;
    done("sms");
  };
  const onTelegram = () => openUrl(`https://t.me/share/url?url=${enc(url || window.location.origin)}&text=${enc(url ? title || "" : bodyFor(3000))}`, "telegram");
  const onX = () => openUrl(`https://twitter.com/intent/tweet?text=${enc(title || L("Résumé SUX1", "SUX1 summary"))}${url ? `&url=${enc(url)}` : ""}`, "x");
  const mobile = isMobile();
  const needLink = url ? undefined : L("Créez d'abord le lien public.", "Create the public link first.");

  const onNative = async () => {
    try {
      await navigator.share({ title: title || "SUX1", text: plain, ...(url ? { url } : {}) });
      done("native");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error(L("Le partage de l'appareil a échoué : utilisez une autre option.", "Device sharing failed: use another option."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase tracking-[0.15em] text-sm">{part ? L("Partager", "Share") : L("Partager ce résumé", "Share this summary")}</DialogTitle>
          <DialogDescription className="text-xs">
            {url ? L("Le destinataire pourra lire le résumé en entier grâce au lien.", "The recipient can read the full summary with the link.") : L("Sans lien public, seul le texte est partagé.", "Without a public link, only the text is shared.")}
          </DialogDescription>
        </DialogHeader>

        {!url && onEnsurePublic && (
          <div className="border border-[#002FA7] bg-[#002FA7]/5 p-3 text-xs" data-testid="share-dialog-public-box">
            <p className="mb-2">{L("Créez un lien public pour que votre destinataire lise le résumé complet (vous pourrez le rendre privé à tout moment).", "Create a public link so your recipient can read the full summary (you can make it private again at any time).")}</p>
            <button
              type="button"
              onClick={createLink}
              disabled={creating || sharing}
              data-testid="share-dialog-create-link-btn"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 bg-[#002FA7] text-white hover:bg-[#001a66] transition-colors disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />} {L("Créer le lien public", "Create the public link")}
            </button>
          </div>
        )}

        {needsAccount && (
          <div className="border border-[#002FA7] bg-[#002FA7]/5 p-3 text-xs" data-testid="share-dialog-account-box">
            <p className="mb-2">
              {L(<>Le <b>lien public</b> est réservé aux comptes (gratuit, 30 secondes) : votre résumé est alors enregistré et le destinataire peut le lire en entier. Sans compte, vous pouvez partager le texte ci-dessous.</>,
                 <>The <b>public link</b> requires an account (free, 30 seconds): your summary is then saved and the recipient can read it in full. Without an account, you can share the text below.</>)}
            </p>
            <a
              href="/signup"
              data-testid="share-dialog-signup-link"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 bg-[#002FA7] text-white hover:bg-[#001a66] transition-colors"
            >
              <Globe className="w-3.5 h-3.5" /> {L("Créer un compte gratuit", "Create a free account")}
            </a>
          </div>
        )}

        {tooLongNoLink && (
          <p className="text-[11px] border border-dashed border-[#FF2A00] text-[#FF2A00] px-3 py-2" data-testid="share-dialog-long-warning">
            {L("Texte long : sur ordinateur, le bouton Email copie tout le texte ; dans l'email, faites Ctrl+V pour le coller.", "Long text: on a computer, the Email button copies the whole text; in the email, press Ctrl+V to paste it.")}
          </p>
        )}

        {url && (
          <div className="border border-border p-3 flex items-center gap-4" data-testid="share-dialog-qr-box">
            <img src={`${API}/qr?data=${encodeURIComponent(url)}`} alt={L("QR code du résumé", "Summary QR code")} className="w-28 h-28 shrink-0" data-testid="share-dialog-qr" />
            <div className="flex-1 text-xs">
              <div className="flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] text-[11px] font-semibold mb-1">
                <QrCode className="w-3.5 h-3.5" /> Partage en classe
              </div>
              <p className="mb-2 text-muted-foreground">{L("Les élèves scannent ce code avec l'appareil photo de leur téléphone pour ouvrir le résumé.", "Pupils scan this code with their phone camera to open the summary.")}</p>
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  setQrFull(true);
                }}
                data-testid="share-dialog-qr-fullscreen-btn"
                data-tip="Afficher le QR code en grand pour le projeter en classe."
                className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 bg-[#002FA7] text-white hover:bg-[#001a66] transition-colors"
              >
                <Maximize2 className="w-3.5 h-3.5" /> {L("Plein écran", "Full screen")}
              </button>
            </div>
          </div>
        )}

        {url && (
          <div className="flex items-center gap-2 border border-border px-3 py-2 text-xs font-mono break-all" data-testid="share-dialog-url">
            <Link2 className="w-3.5 h-3.5 shrink-0" /> {url}
          </div>
        )}

        <div className="space-y-2">
          {url && <Option icon={Copy} label="Copier le lien" onClick={onCopyLink} testid="share-dialog-copy-link" />}
          <Option icon={Mail} label="Email" hint={L("Ouvre votre messagerie avec le résumé", "Opens your email app with the summary")} onClick={onEmail} testid="share-dialog-email" />
          <Option icon={MessageCircle} label="WhatsApp" onClick={onWhatsApp} testid="share-dialog-whatsapp" />
          {canNative && <Option icon={Share2} label={L("Autres applications", "Other apps")} hint={L("Menu de partage de votre appareil", "Your device's share menu")} onClick={onNative} testid="share-dialog-native" />}
          <Option icon={FileText} label={L("Copier le texte complet", "Copy full text")} onClick={onCopyText} testid="share-dialog-copy-text" />
        </div>

        <div data-testid="share-dialog-networks">
          <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-2">{L("Réseaux et messageries", "Social networks and messaging")}</div>
          <div className="grid grid-cols-3 gap-2">
            <Network label="Gmail" onClick={onGmail} testid="share-dialog-gmail" />
            <Network label="Facebook" onClick={onFacebook} testid="share-dialog-facebook" disabled={!url} hint={needLink || "Publier le lien sur Facebook."} />
            {mobile && <Network label="Messenger" onClick={onMessenger} testid="share-dialog-messenger" disabled={!url} hint={needLink || L("Envoyer le lien par Messenger.", "Send the link via Messenger.")} />}
            {mobile && <Network label="SMS" onClick={onSms} testid="share-dialog-sms" />}
            <Network label="Telegram" onClick={onTelegram} testid="share-dialog-telegram" />
            <Network label="X (Twitter)" onClick={onX} testid="share-dialog-x" />
          </div>
          {!url && !needsAccount && (
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="share-dialog-networks-hint">
              {L("Facebook et Messenger partagent un lien : créez d'abord le lien public ci-dessus.", "Facebook and Messenger share a link: create the public link above first.")}
            </p>
          )}
        </div>
      </DialogContent>
      {qrFull && url && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-white flex flex-col items-center justify-center p-6 text-center"
          data-testid="share-qr-fullscreen"
          onClick={() => setQrFull(false)}
        >
          <img src={`${API}/qr?data=${encodeURIComponent(url)}`} alt={L("QR code du résumé", "Summary QR code")} className="w-[min(80vw,70vh)] h-[min(80vw,70vh)]" />
          <div className="mt-6 text-2xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>{title}</div>
          <div className="mt-2 font-mono text-sm text-muted-foreground">Scannez avec l&apos;appareil photo · sux1.app</div>
          <button
            type="button"
            onClick={() => setQrFull(false)}
            data-testid="share-qr-fullscreen-close"
            className="mt-6 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#002FA7] hover:text-white"
          >
            Fermer
          </button>
        </div>,
        document.body
      )}
    </Dialog>
  );
}

export default ShareDialog;
